import { getToken } from './auth';
import type {
  Project,
  DocumentMeta,
  DocVersion,
  Link,
  VisitSummary,
  NdaSignature,
  DocVersionAnalytics,
  FeedResponse,
  ProjectVisitsResponse,
} from './types';

const AUTH_URL = 'https://auth.redwoodoperations.com';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 401) {
    const redirect = encodeURIComponent(window.location.href);
    window.location.href = `${AUTH_URL}?redirect=${redirect}`;
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  bootstrap: () => request<{ user: unknown; projects: Project[] }>('/api/bootstrap'),
  feed: () => request<FeedResponse>('/api/feed'),
  projectVisits: (pid: string) =>
    request<ProjectVisitsResponse>(`/api/projects/${pid}/visits`),
  docThumbs: (pid: string, docId: string, version: number) =>
    request<{ thumbs: string[] }>(
      `/api/projects/${pid}/docs/${docId}/thumbs?version=${version}`,
    ),

  createProject: (input: { name: string; description?: string }) =>
    request<{ project: Project }>('/api/projects', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  getProject: (pid: string) =>
    request<{
      project: Project;
      docs: DocumentMeta[];
      links: Link[];
      ndaSignatures: NdaSignature[];
    }>(`/api/projects/${pid}`),
  patchProject: (pid: string, input: { name?: string; description?: string }) =>
    request<{ project: Project }>(`/api/projects/${pid}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteProject: (pid: string) =>
    request<void>(`/api/projects/${pid}`, { method: 'DELETE' }),

  createDoc: (
    pid: string,
    input: {
      title: string;
      contentType: string;
      fileName: string;
      sizeBytes: number;
      kind?: 'doc' | 'nda';
    },
  ) =>
    request<{ doc: DocumentMeta; version: DocVersion; uploadUrl: string }>(
      `/api/projects/${pid}/docs`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  createVersion: (
    pid: string,
    docId: string,
    input: { contentType: string; fileName: string; sizeBytes: number },
  ) =>
    request<{ version: DocVersion; uploadUrl: string }>(
      `/api/projects/${pid}/docs/${docId}/versions`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  ingestVersion: (pid: string, docId: string, version: number) =>
    request<{ status: string }>(
      `/api/projects/${pid}/docs/${docId}/versions/${version}/ingest`,
      { method: 'POST' },
    ),
  getDoc: (pid: string, docId: string) =>
    request<{ doc: DocumentMeta; versions: DocVersion[] }>(
      `/api/projects/${pid}/docs/${docId}`,
    ),
  patchDoc: (pid: string, docId: string, input: { title: string }) =>
    request<{ doc: DocumentMeta }>(`/api/projects/${pid}/docs/${docId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteDoc: (pid: string, docId: string) =>
    request<void>(`/api/projects/${pid}/docs/${docId}`, { method: 'DELETE' }),
  docAnalytics: (pid: string, docId: string) =>
    request<{ doc: DocumentMeta; analytics: DocVersionAnalytics[] }>(
      `/api/projects/${pid}/docs/${docId}/analytics`,
    ),
  downloadOriginal: (pid: string, docId: string, version: number) =>
    request<{ url: string }>(
      `/api/projects/${pid}/docs/${docId}/versions/${version}/download`,
    ),

  createLink: (
    pid: string,
    input: {
      label: string;
      docIds: string[];
      requireEmail?: boolean;
      requireNda?: boolean;
      allowDownload?: boolean;
      expiresAt?: string | null;
    },
  ) =>
    request<{ link: Link }>(`/api/projects/${pid}/links`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  patchLink: (pid: string, linkId: string, input: Partial<Link>) =>
    request<{ link: Link }>(`/api/projects/${pid}/links/${linkId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteLink: (pid: string, linkId: string) =>
    request<void>(`/api/projects/${pid}/links/${linkId}`, { method: 'DELETE' }),
  linkVisits: (pid: string, linkId: string) =>
    request<{
      link: Link;
      visits: VisitSummary[];
      raw: { visitId: string; pageSeconds: Record<string, number> }[];
    }>(`/api/projects/${pid}/links/${linkId}/visits`),

  ndaSignatures: (pid: string) =>
    request<{ signatures: NdaSignature[] }>(`/api/projects/${pid}/ndas`),
  ndaDownload: (pid: string, signatureId: string) =>
    request<{ url: string }>(`/api/projects/${pid}/ndas/${signatureId}/download`),
};

/** PUT a file straight to S3 via a presigned URL. */
export async function uploadToS3(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
}
